import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Form, Input, Checkbox } from "antd";
import { Link } from "react-router-dom";
import { CustomTypography, CustomButton } from "src/components/ui";
import { cn } from "src/util/cn";
import { useLoginContainer } from "./useLoginContainer";
import type { LoginFormValues } from "src/types/common.types";
import TyreParticles from "src/components/holiday/TyreParticles";
import Snowfall from "src/components/holiday/Snowfall";
import GarlandLights from "src/components/holiday/GarlandLights";
import { CustomOtp } from "src/components/auth/CustomOtp";
import { OtpTimer } from "src/components/auth/OtpTimer";
import {
  getDistributorCode,
  getDistributorLogo,
} from "src/util/distributorBranding.service";
import { ForgotPassword } from "./ForgotPassword";

import "./Login.css";

const Login: React.FC = () => {
  const [form] = Form.useForm<LoginFormValues>();
  const [isForgetScreen, setIsForgetScreen] = useState(false);
  const logo = getDistributorLogo();
  const isVei = getDistributorCode() === "vei";

  const {
    showClientId,
    userName,
    startTimer,
    otp,
    setOtp,
    otpRequestLoader,
    setStartTimer,
    isShowOtp,
    setUserName,
    handleSubmit,
    loginRequestLoader,
    otpRequest,
    sessionId,
  } = useLoginContainer();

  useEffect(() => {
    const previousBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = "#e1e1e1";
    return () => {
      document.body.style.backgroundColor = previousBg;
    };
  }, []);

  const today = useMemo(() => new Date(), []);
  const holidayThemeEnabled = useMemo(() => {
    const month = today.getMonth();
    const day = today.getDate();
    return (month === 11 && day >= 6) || (month === 0 && day <= 7);
  }, [today]);

  const cardClasses = useMemo(
    () =>
      cn(
        "relative z-[1000] box-border w-full max-w-[680px] rounded-[10px] overflow-hidden",
        holidayThemeEnabled
          ? "border border-white/50 shadow-[0_12px_45px_rgba(0,0,0,0.18)]"
          : "bg-white"
      ),
    [holidayThemeEnabled]
  );

  const cardStyle = holidayThemeEnabled
    ? {
        background: "rgba(255, 255, 255, 0.78)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
      }
    : undefined;

  const onFinish = (values: LoginFormValues) => {
    setUserName(values.userName);
    handleSubmit(values);
  };

  const onResendHandler = useCallback(async () => {
    if (otpRequestLoader) return false;
    try {
      const response = await otpRequest({ username: userName });
      if (response?.data) {
        setStartTimer(true);
        return true;
      }
    } catch {
      return false;
    }
    return false;
  }, [otpRequest, otpRequestLoader, setStartTimer, userName]);

  const onExpireHandler = useCallback(() => {
    setStartTimer(false);
  }, [setStartTimer]);

  return (
    <div className="relative flex h-screen items-center justify-center px-4">
      <TyreParticles />
      {holidayThemeEnabled && <Snowfall />}

      <div className={cardClasses} style={cardStyle}>
        {holidayThemeEnabled && <GarlandLights />}

        <div className="login-card-shell relative z-[2] w-full p-8 sm:p-10">
        {isForgetScreen ? (
          <ForgotPassword isForgetScreen={isForgetScreen} setIsForgetScreen={setIsForgetScreen} />
        ) : (
          <>
            <div className="flex flex-col items-center text-center gap-[2px] pb-8">
              <div className="flex justify-center mb-[4px]">
                <img
                  src={logo}
                  className={isVei ? "w-[180px] max-w-full h-auto" : "w-[76px] h-auto"}
                  alt="logo"
                />
              </div>
              <div>
                <CustomTypography.CustomTitle className="!m-0 !p-0 !text-[1.5rem] !leading-[1.2]">
                  CRM
                </CustomTypography.CustomTitle>
                <CustomTypography.CustomText className="!m-0 !p-0 !text-[0.875rem] !leading-[1.45] !font-[Inter_var] !font-[500]">
                  CLIENT RELATION MANAGEMENT
                </CustomTypography.CustomText>
              </div>
              <CustomTypography.CustomText className="!mt-3 !mb-0 !p-0 !text-[0.875rem] !leading-[1.57] !font-[Inter_var] !font-[500] !text-[#485056]">
                {!isShowOtp
                  ? "Sign in to continue to CRM"
                  : "We have send a verifiaction code to your email address please enter it to continue."}
              </CustomTypography.CustomText>
            </div>

            <Form<LoginFormValues>
              form={form}
              layout="vertical"
              onFinish={onFinish}
              autoComplete="off"
              className="login-form-container"
              initialValues={{ remember: false }}
            >
              {!isShowOtp && (
                <>
                  <Form.Item
                    name="userName"
                    className="!mb-3"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your Username or email address",
                      },
                    ]}
                  >
                    <Input
                      aria-label="Username or email"
                      className="!h-[50px] !text-[14px]"
                      placeholder="Username or email"
                    />
                  </Form.Item>

                  {showClientId && (
                    <Form.Item
                      name="clientId"
                      className="!mb-3"
                      rules={[{ required: true, message: "Please input your client ID!" }]}
                    >
                      <Input
                        aria-label="Client ID"
                        className="!h-[50px] !text-[14px]"
                        placeholder="Enter your client ID"
                      />
                    </Form.Item>
                  )}

                  <Form.Item
                    name="password"
                    className="!mb-0"
                    rules={[{ required: true, message: "Please input your password!" }]}
                  >
                    <Input.Password
                      aria-label="Password"
                      className="!h-[50px] !text-[14px]"
                      placeholder="Enter your password"
                    />
                  </Form.Item>
                </>
              )}

              {isShowOtp && <CustomOtp otp={otp} setOtp={setOtp} />}

              {!isShowOtp && (
                <Form.Item name="remember" valuePropName="checked" className="!mb-5 !mt-4">
                  <Checkbox className="select-none">Remember me</Checkbox>
                </Form.Item>
              )}

              {isShowOtp && !otpRequestLoader && (
                <div className="text-center w-full mb-3">
                  <OtpTimer
                    start={startTimer}
                    minutes={5}
                    onResend={onResendHandler}
                    onExpire={onExpireHandler}
                    sendButtonText={otpRequestLoader ? "Sending..." : "Send New Code"}
                  />
                </div>
              )}

              <Form.Item className="!mb-0">
                <CustomButton
                  type="primary"
                  buttonSize="big"
                  htmlType="submit"
                  label={!isShowOtp ? "Log In" : "Verify"}
                  loading={otpRequestLoader || loginRequestLoader}
                  disabled={isShowOtp && (otp.length === 0 || !sessionId)}
                  className="!text-white !bg-[#001b3a] w-full disabled:opacity-60 disabled:cursor-not-allowed disabled:!text-white/70"
                />
              </Form.Item>
            </Form>

            {!isForgetScreen && !isShowOtp && (
              <div className="mt-3 text-left">
                <Link
                  to="#"
                  className="text-[14px] !text-[#000000e0]"
                  onClick={(event) => {
                    event.preventDefault();
                    setIsForgetScreen(true);
                  }}
                >
                  Forgot Password?
                </Link>
              </div>
            )}
          </>
        )}
        </div>
      </div>
    </div>
  );
};

export default Login;
